import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';

export type WorkspaceEnvironment = 'development' | 'staging' | 'production';
export type WorkspaceFeatureKey =
  | 'virtualLists'
  | 'analytics'
  | 'errorMonitoring';

export interface WorkspaceState {
  id: string;
  name: string;
  environment: WorkspaceEnvironment;
  features: Record<WorkspaceFeatureKey, boolean>;
}

const STORAGE_KEY = 'workspace_settings';

const defaultState = (): WorkspaceState => ({
  id: 'default',
  name: 'Default Workspace',
  environment: 'development',
  features: {
    virtualLists: true,
    analytics: true,
    errorMonitoring: true
  }
});

export const useWorkspaceStore = defineStore('workspace', () => {
  const workspace = reactive<WorkspaceState>(loadState());

  const currentWorkspace = computed(() => workspace);

  const setWorkspace = (next: Partial<WorkspaceState>): void => {
    if (next.id && next.id.trim().length > 0) {
      workspace.id = next.id.trim();
    }
    if (next.name && next.name.trim().length > 0) {
      workspace.name = next.name.trim();
    }
    if (next.environment) {
      workspace.environment = next.environment;
    }
    if (next.features) {
      Object.entries(next.features).forEach(([key, value]) => {
        if (isWorkspaceFeatureKey(key)) {
          workspace.features[key] = Boolean(value);
        }
      });
    }
    persistState(workspace);
  };

  const setFeature = (feature: WorkspaceFeatureKey, enabled: boolean): void => {
    workspace.features[feature] = enabled;
    persistState(workspace);
  };

  const toggleFeature = (feature: WorkspaceFeatureKey): void => {
    workspace.features[feature] = !workspace.features[feature];
    persistState(workspace);
  };

  const isFeatureEnabled = (feature: WorkspaceFeatureKey): boolean => {
    return Boolean(workspace.features[feature]);
  };

  const initialize = (state?: WorkspaceState): void => {
    const payload = state ?? loadState();
    setWorkspace(payload);
  };

  return {
    currentWorkspace,
    setWorkspace,
    setFeature,
    toggleFeature,
    isFeatureEnabled,
    initialize
  };
});

const isWorkspaceFeatureKey = (value: string): value is WorkspaceFeatureKey => {
  return value === 'virtualLists' || value === 'analytics' || value === 'errorMonitoring';
};

const loadState = (): WorkspaceState => {
  if (typeof window === 'undefined') {
    return defaultState();
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return defaultState();
    }
    const parsed = JSON.parse(stored) as Partial<WorkspaceState>;
    return {
      ...defaultState(),
      ...parsed,
      features: {
        ...defaultState().features,
        ...(parsed.features ?? {})
      }
    };
  } catch (error) {
    console.error('[workspaceStore] Failed to load workspace settings.', { error });
    return defaultState();
  }
};

const persistState = (state: WorkspaceState): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('[workspaceStore] Failed to persist workspace settings.', { error });
  }
};

